// Cloudflare Turnstile for POST /v1/jobs (IMPL_ADDENDUM §2a).
//   useTurnstile({ enabled, sitekey, theme }) → { ref, token, status, error, interactive, reset(), retry() }
//     ref: callback ref for the widget slot (the run sheet's "Check" row)
//     status: off · loading (script) · checking · verified · failed
//     token: the current single-use token, or null; the submit CTA stays disabled without one
//     reset(): after EVERY submit attempt (tokens are single-use) · retry(): after a failure (reloads the script if needed)
// The script loads once, only when enabled; the widget is interaction-only (invisible unless Cloudflare wants a
// click), flexible width, action "submit", re-rendered on theme change and removed on unmount.
import { useCallback, useEffect, useRef, useState } from "react";

type RenderOptions = {
  sitekey: string;
  theme: "light" | "dark";
  appearance: "interaction-only";
  size: "flexible";
  action: string;
  callback: (token: string) => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
  "error-callback": (code: string) => boolean;
  "before-interactive-callback": () => void;
  "after-interactive-callback": () => void;
};

type TurnstileApi = {
  render: (el: HTMLElement, opts: RenderOptions) => string | null | undefined;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loading: Promise<TurnstileApi> | null = null;

function api(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

function loadTurnstile(): Promise<TurnstileApi> {
  const ready = api();
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      const t = api();
      if (t) resolve(t);
      else reject(new Error("the Cloudflare script loaded without Turnstile"));
    };
    s.onerror = () => {
      s.remove();
      reject(new Error("couldn't reach challenges.cloudflare.com"));
    };
    document.head.appendChild(s);
  });
  loading.catch(() => {
    loading = null; // a later retry loads it again
  });
  return loading;
}

export type TurnstileStatus = "off" | "loading" | "checking" | "verified" | "failed";

/** Cloudflare's client-side error codes, in words (developers.cloudflare.com/turnstile/troubleshooting/client-side-errors). */
function turnstileErrorText(code: string): string {
  const c = code.trim();
  if (c === "110100" || c === "110110") return "the site key is not valid";
  if (c === "110200") return "this domain is not allowed for the site key";
  if (c === "110500") return "this browser is not supported";
  if (c === "110600" || c === "110620") return "the check timed out";
  if (c === "200100") return "this computer's clock is off";
  if (/^(300|600)\d{3}$/.test(c)) return `Cloudflare could not verify this browser (${c})`;
  return c ? `error ${c}` : "unknown error";
}

export function useTurnstile({ enabled, sitekey, theme }: { enabled: boolean; sitekey?: string; theme: "light" | "dark" }) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(enabled ? "loading" : "off");
  const [error, setError] = useState<string | null>(null);
  const [interactive, setInteractive] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);

  useEffect(() => {
    if (!enabled || !sitekey) {
      setStatus(enabled ? "loading" : "off");
      setToken(null);
      return;
    }
    if (!slot) return;
    let live = true;
    setStatus("loading");
    setToken(null);
    setError(null);
    loadTurnstile().then(
      (ts) => {
        if (!live) return;
        setStatus("checking");
        const id = ts.render(slot, {
          sitekey,
          theme,
          appearance: "interaction-only",
          size: "flexible",
          action: "submit",
          callback: (t) => {
            if (!live) return;
            setToken(t);
            setError(null);
            setStatus("verified");
          },
          "expired-callback": () => {
            if (!live) return;
            setToken(null);
            setStatus("checking"); // refresh-expired "auto" fetches a new one
          },
          "timeout-callback": () => {
            if (!live) return;
            setToken(null);
            setStatus("checking");
          },
          "error-callback": (code) => {
            if (live) {
              setToken(null);
              setError(turnstileErrorText(String(code ?? "")));
              setStatus("failed");
            }
            return true; // handled: no uncaught error from the widget
          },
          "before-interactive-callback": () => live && setInteractive(true),
          "after-interactive-callback": () => live && setInteractive(false),
        });
        widget.current = id ? { api: ts, id } : null;
      },
      (err: unknown) => {
        if (!live) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("failed");
      },
    );
    return () => {
      live = false;
      const w = widget.current;
      widget.current = null;
      setInteractive(false);
      if (w) {
        try {
          w.api.remove(w.id);
        } catch {
          /* already gone */
        }
      }
    };
  }, [enabled, sitekey, slot, theme, attempt]);

  const reset = useCallback(() => {
    setToken(null);
    const w = widget.current;
    if (!w) return;
    setStatus("checking");
    try {
      w.api.reset(w.id);
    } catch {
      setAttempt((a) => a + 1);
    }
  }, []);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return { ref: setSlot, token, status, error, interactive, reset, retry };
}
